'use client'

import React from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  v2ModalChipClassName,
  v2ModalChipStyle,
  v2ModalFieldStyle,
  v2ModalInputClassName,
  v2ModalSelectClassName,
  v2ModalSelectStyle,
  v2ModalStyles,
  v2ModalTextareaClassName,
} from '@/lib/design/v2ModalContent'
import { playV2FocusRing } from '@/lib/play/playV2Focus'

export function WagamourV2ModalEyebrow({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <p
      className={cn('mb-1 text-[10px] font-semibold uppercase tracking-[0.14em]', className)}
      style={v2ModalStyles.eyebrow}
    >
      {children}
    </p>
  )
}

export function WagamourV2ModalHeadline({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <h2 className={cn('text-lg font-bold leading-snug', className)} style={v2ModalStyles.headline}>
      {children}
    </h2>
  )
}

export function WagamourV2ModalStoryLine({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div>
      <WagamourV2ModalEyebrow>{label}</WagamourV2ModalEyebrow>
      <div className="text-sm font-medium leading-snug" style={v2ModalStyles.body}>
        {children}
      </div>
    </div>
  )
}

export function WagamourV2ModalNestedCard({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className={cn('rounded-[20px] border p-3', className)} style={v2ModalStyles.nestedCard} data-surface-fg="dark-glass">
      {children}
    </div>
  )
}

export function WagamourV2ModalFieldLabel({
  children,
  className,
  ...props
}: React.LabelHTMLAttributes<HTMLLabelElement>) {
  return (
    <label
      className={cn('mb-1 block text-[10px] font-semibold uppercase tracking-[0.12em]', className)}
      style={v2ModalStyles.eyebrow}
      {...props}
    >
      {children}
    </label>
  )
}

export function WagamourV2ModalIconWell({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        'flex h-12 w-12 shrink-0 items-center justify-center rounded-full border backdrop-blur-[12px]',
        className
      )}
      style={{
        background: 'linear-gradient(165deg, rgba(251, 191, 36, 0.22) 0%, rgba(251, 146, 60, 0.14) 100%)',
        borderColor: 'rgba(255, 236, 210, 0.32)',
        boxShadow: 'inset 0 1px 0 rgba(255, 248, 238, 0.28)',
        color: v2ModalStyles.warm.color,
      }}
    >
      {children}
    </div>
  )
}

export function WagamourV2ModalInput({
  className,
  style,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      className={cn(v2ModalInputClassName, className)}
      style={{ ...v2ModalFieldStyle(), ...style }}
      {...props}
    />
  )
}

export function WagamourV2ModalTextarea({
  className,
  style,
  ...props
}: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      className={cn(v2ModalTextareaClassName, className)}
      style={{ ...v2ModalFieldStyle(), ...style }}
      {...props}
    />
  )
}

export function WagamourV2ModalSelect({
  className,
  style,
  children,
  ...props
}: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <div className="relative">
      <select
        className={cn(v2ModalSelectClassName, className)}
        style={{ ...v2ModalSelectStyle(), ...style }}
        {...props}
      >
        {children}
      </select>
      <ChevronDown
        className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/55"
        aria-hidden
      />
    </div>
  )
}

export function WagamourV2ModalChipToggle({
  label,
  selected,
  onToggle,
  className,
}: {
  label: string
  selected: boolean
  onToggle: () => void
  className?: string
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={selected}
      className={cn(v2ModalChipClassName(selected), playV2FocusRing, className)}
      style={v2ModalChipStyle(selected)}
    >
      {label}
    </button>
  )
}
